import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { catchError, throwError } from 'rxjs';
import { authenticationBaseUrl, environment } from '../../../environments/environment';
import { ApiErrorResponse } from '../http/api-error.model';
import { AuthState } from '../../features/auth/auth.state';
import { AuthService } from './auth.service';
import { SessionExpiredService } from './session-expired.service';

function isAuthApiRequest(url: string): boolean {
    return (
        url.includes(`${authenticationBaseUrl}/login`) ||
        url.includes(`${authenticationBaseUrl}/session`) ||
        url.includes(`${authenticationBaseUrl}/logout`) ||
        url.includes(`${authenticationBaseUrl}/change-password`) ||
        url.includes(`${authenticationBaseUrl}/register`)
    );
}

/** Temporary diagnostics: dev always (sessionDiag), prod via sessionStorage rp_session_diag=1 */
function sessionDiagLog(payload: Record<string, unknown>): void {
    const enabled =
        environment.sessionDiag ||
        (typeof sessionStorage !== 'undefined' &&
            sessionStorage.getItem('rp_session_diag') === '1');
    if (!enabled) return;
    console.info('[rp-session-diag]', payload);
}

export const authUnauthorizedInterceptor: HttpInterceptorFn = (req, next) => {
    const sessionExpired = inject(SessionExpiredService);
    const auth = inject(AuthService);
    const store = inject(Store);

    const isAuthEndpoint = isAuthApiRequest(req.url);

    return next(req).pipe(
        catchError((err: ApiErrorResponse) => {
            if (!(err instanceof HttpErrorResponse)) {
                return throwError(() => err);
            }

            if (err.status !== 401 && err.status !== 403) {
                return throwError(() => err);
            }

            const ngxSessionStatus = store.selectSnapshot(AuthState.sessionStatus);
            const wasAuthenticated =
                auth.status === 'authenticated' ||
                ngxSessionStatus === 'authenticated';

            const showSessionExpiredModal =
                !isAuthEndpoint && err.status === 401 && wasAuthenticated;

            sessionDiagLog({
                url: req.url,
                status: err.status,
                withCredentials: req.withCredentials === true,
                isAuthEndpoint,
                authServiceStatus: auth.status,
                ngxSessionStatus,
                wasAuthenticated,
                showSessionExpiredModal,
            });

            if (showSessionExpiredModal) {
                auth.clearLocalSession();
                void sessionExpired.handleSessionExpired();
            } else if (err.status === 401 && !isAuthEndpoint) {
                // Anonymous / bootstrap: sync local auth flag without modal
                auth.clearLocalSession();
            }

            return throwError(() => err);
        })
    );
};
